/** Fanar client. Foreground-only by design: background geolocation does not exist
 *  on the web on either platform, so the product is "phone in a cradle, screen on". */
import type maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

import { AlertEngine, clipUrl, navClipUrl, type Alert, type Fix, type Visible } from "../shared/alerts.js";
import { decayState, SEVERITY_STYLE } from "../shared/decay.js";
import { encodeGeohash } from "../shared/geohash.js";
import { haversine } from "../shared/geo.js";
import { HAZARDS, HAZARD_TYPES, pickProfile, type HazardType } from "../shared/hazards.js";
import { VoiceAlerts } from "./lib/audio.js";
import { WakeLock } from "./lib/wakelock.js";
import { Session } from "./lib/session.js";
import { Feed } from "./lib/feed.js";
import { pinMark, glyphMark, setRing, MARK_F, MARK_VEHICLE, MARK_REPORT } from "./lib/marks.js";
import { Radial, overflowTypes } from "./ui/radial.js";
import { Screens } from "./ui/screens.js";
import { RouteGuide, wireSearch, NAV_FRAGMENTS, type Destination } from "./lib/nav.js";
import { pointAlong, type RouteIndex } from "../shared/route.js";
import { SpeedLimits } from "./lib/limits.js";
import { TrafficSampler, installTrafficLayers, routeDelayS, trafficGeoJson } from "./lib/traffic.js";
import { STYLE_URL, LABEL_LAYERS } from "./lib/mapstyle.js";
import { beamPolygon } from "./lib/beam.js";

const params = new URLSearchParams(location.search);
const APP_VERSION = "0.3.0";
/** `?api=` / `?snap=` let a test harness point the build at its own stubs. Without
 *  this the endpoints are baked in at build time and a harness silently tests
 *  whatever the real backend happens to hold. */
// Production (.env.production) is same-origin: API at "", snapshots at "/snap".
const API_BASE = params.get("api") ?? import.meta.env["VITE_API_BASE"] ?? "";
const SNAPSHOT_BASE = params.get("snap") ?? import.meta.env["VITE_SNAPSHOT_BASE"] ?? "/snap";

const $ = <T extends HTMLElement>(id: string): T => {
  const el = document.getElementById(id);
  if (el === null) throw new Error(`missing element #${id}`);
  return el as T;
};

const voice = new VoiceAlerts();
const wakeLock = new WakeLock();
const session = new Session(API_BASE);
const feed = new Feed(SNAPSHOT_BASE);
const engine = new AlertEngine();
const limits = new SpeedLimits();
const trafficSampler = new TrafficSampler(API_BASE);
const radial = new Radial($("radial"));

/** Test hook for the drive-replay harness. Defined at module scope so the harness
 *  can stage fixtures before the drive begins, and so a blank map — the failure
 *  mode that matters most here — is diagnosable from outside. */
(window as unknown as { __fanar?: unknown }).__fanar = {
  engine, feed, voice, session, encodeGeohash,
  get map(): maplibregl.Map | null { return map; },
  get nav(): RouteGuide { return nav; },
  limits, trafficSampler,
  goTo: (d: Destination) => startNavigation(d),
};

let map: maplibregl.Map | null = null;

async function castVote(alertId: string, vote: "confirm" | "reject"): Promise<boolean> {
  if (fix === null) return false;
  const body = { vote, lat: fix.lat, lon: fix.lon, gh6TenMinAgo: lastGh6?.cell ?? null };
  const res = await session.signed(`/v1/reports/${alertId}/votes`, body);
  if (res === null) {
    session.enqueue(`/v1/reports/${alertId}/votes`, body); // offline: it still counts later
    return false;
  }
  return res.status === 200;
}

const screens = new Screens({
  session,
  voice,
  getMap: () => map,
  getAll: () => engine.all(),
  onBrowse: (on) => {
    browsing = on;
    if (map !== null) {
      for (const id of ["beam-fill", "beam-edge"]) {
        if (map.getLayer(id) !== undefined) map.setLayoutProperty(id, "visibility", on ? "none" : "visible");
      }
    }
    if (fix !== null) { render(fix); if (!on) onFix(lastPos as GeolocationPosition); }
  },
  onEndDrive: () => endDrive(),
  vote: castVote,
});
/* ---------------- navigation ---------------- */

/** The simulator follows the route when there is one: metres along at the tick
 *  the route was set, so the sim car does not jump. */
let simRoute: { idx: RouteIndex; alongAt: number; tick: number } | null = null;

const nav = new RouteGuide({
  session,
  delayAhead: (idx, alongM) => routeDelayS(idx, alongM, feed.traffic),
  getMap: () => map,
  getGl: () => gl,
  onRoute: (idx) => {
    $("drive").dataset["nav"] = idx === null ? "0" : "1";
    if (idx === null) { engine.setAhead(null); simRoute = null; return; }
    // Fill the card now. A parked car gets no new GPS fix for minutes, and an
    // empty white card was the first thing seen on a real desktop.
    if (fix !== null) nav.tick(fix);
    if (SIM && fix !== null) {
      // Re-base the simulated drive onto the route from where the car is now.
      const { alongM } = projectOnRouteSafe(idx, fix);
      simRoute = { idx, alongAt: alongM, tick: simSeconds };
    }
  },
  onArrive: () => { engine.setAhead(null); },
});

function projectOnRouteSafe(idx: RouteIndex, p: { lat: number; lon: number }): { alongM: number } {
  // The route module's projection; kept behind a tiny wrapper so main.ts
  // depends on one symbol.
  let best = Infinity, along = 0;
  for (let i = 0; i < idx.route.geometry.length; i++) {
    const g = idx.route.geometry[i] as readonly [number, number];
    const d = haversine(p, { lat: g[0], lon: g[1] });
    if (d < best) { best = d; along = idx.cum[i] as number; }
  }
  return { alongM: along };
}

async function startNavigation(dest: Destination): Promise<void> {
  if (fix === null) return;
  driveStats.navigated = true;
  const err = await nav.start(fix, dest);
  if (err !== null) {
    // Show the reason where the card would be, for six seconds.
    const card = $("navCard");
    $("navGlyph").innerHTML = "";
    $("navDistance").textContent = dest.name;
    $("navText").textContent = err;
    $("navRoad").hidden = true;
    $("navMeta").textContent = "";
    card.hidden = false;
    window.setTimeout(() => { if (!nav.active) card.hidden = true; }, 6000);
  }
}

nav.setVoice((kind) => { voice.chime(kind); }, (urls) => voice.speakSequence(urls));
wireSearch({ session, getFix: () => fix, onPick: (d) => void startNavigation(d) });

/* ---------------- free look ---------------- */

/** After the driver pans or zooms, the camera stops following until they tap
 *  "Qendro" or eight seconds pass without another touch. */
let freeLook = false;
let freeLookTimer: number | null = null;

function onUserMovedMap(): void {
  if (browsing) return;
  freeLook = true;
  $("recenter").hidden = false;
  if (freeLookTimer !== null) window.clearTimeout(freeLookTimer);
  freeLookTimer = window.setTimeout(recenter, 8000);
}

function recenter(): void {
  freeLook = false;
  $("recenter").hidden = true;
  if (freeLookTimer !== null) { window.clearTimeout(freeLookTimer); freeLookTimer = null; }
  if (fix !== null && lastPos !== null) onFix(lastPos);
}
$("recenter").addEventListener("click", recenter);

// While guiding, the card's clock and ETA move even when the car does not: a
// parked car may not produce a new fix for minutes.
window.setInterval(() => {
  if (fix === null || $("drive").hidden) return;
  const now = Date.now();
  if (now - fix.at > 1500) {
    // No fresh fix: the car is parked (or the receiver is quiet). Keep the
    // parts that depend on time, not position, moving.
    updateStationary(fix, now);
    if (nav.active) nav.tick({ ...fix, at: now });
  }
}, 1000);

let vehicle: maplibregl.Marker | null = null;
/** Loaded lazily: the welcome screen must paint without waiting for the map engine,
 *  which is ~1 MB of the bundle. */
let gl: typeof maplibregl | null = null;
let fix: Fix | null = null;
let lastGh6: { cell: string; at: number } | null = null;
const markers = new Map<string, maplibregl.Marker>();
let syncing = false;
let watchId: number | null = null;
/** Harta open: the map is unlocked, the camera stops following, all pins draw. */
let browsing = false;
let stoppedSince: number | null = null;
/** The hazard the band is currently showing — the target of a swipe vote. */
let activeAlert: Alert | null = null;

const isIOS = /iP(hone|ad|od)/.test(navigator.userAgent);
const isStandalone =
  window.matchMedia("(display-mode: standalone)").matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

/* ---------------- start of drive ---------------- */

$("start").addEventListener("click", () => {
  // MUST be first and synchronous: on iOS the gesture only unlocks the element
  // and context created inside it. Everything else can wait for a microtask.
  voice.unlockFromGesture();
  void beginDrive();
});

async function beginDrive(): Promise<void> {
  // iOS has no install prompt API, so the coach is a screen, shown once.
  // `?coach=1` forces the install coach so it can be reviewed off-device.
  if ((isIOS && !isStandalone && localStorage.getItem("fanar.coached") === null) || params.get("coach") !== null) {
    localStorage.setItem("fanar.coached", "1");
    $("welcome").hidden = true;
    $("installCoach").hidden = false;
    return;
  }
  enterDrive();
}

$("coachDismiss").addEventListener("click", () => {
  $("installCoach").hidden = true;
  enterDrive();
});

function enterDrive(): void {
  $("welcome").hidden = true;
  $("installCoach").hidden = true;
  $("drive").hidden = false;

  engine.resetAnnouncements();
  driveStats.startedAt = Date.now(); driveStats.alerts = 0; driveStats.reports = 0; driveStats.navigated = false;
  void wakeLock.acquire();
  // The road graph downloads once and lives in the cache; start now so the
  // first "Ku shkon?" answers from the phone, not from a server.
  void nav.local.preload();
  void session.ensureDevice().then(() => session.flush());
  void voice.preload(
    // Info types are drawn, never spoken: no clip exists for them, so do not ask.
    [...HAZARD_TYPES.filter((t) => HAZARDS[t].severity !== "info").flatMap((t) => [clipUrl(t, "primary"), clipUrl(t, "secondary")]), ...NAV_FRAGMENTS.map((f) => navClipUrl(f))],
  );
  void initMap();
  startWatch();
}

async function initMap(): Promise<void> {
  if (map !== null) return;
  gl = (await import("maplibre-gl")).default;
  map = new gl.Map({
    container: "map",
    style: STYLE_URL,
    center: [19.8187, 41.3275],
    zoom: 14,
    attributionControl: { compact: true },
    // Pan and zoom are allowed (free look, auto-recentre); rotation is not — the
    // map is heading-up and a driver must never be able to leave it turned.
    interactive: true,
    dragRotate: false,
    pitchWithRotate: false,
    fadeDuration: 0,
  });
  map.touchZoomRotate.disableRotation();
  map.keyboard.disable();
  for (const ev of ["dragstart", "zoomstart"] as const) {
    map.on(ev, (e: { originalEvent?: Event }) => { if (e.originalEvent !== undefined) onUserMovedMap(); });
  }

  const el = document.createElement("div");
  el.className = "vehicle";
  el.innerHTML = MARK_VEHICLE;
  vehicle = new gl.Marker({ element: el, rotationAlignment: "map" })
    .setLngLat([19.8187, 41.3275]);
  // Layer setup runs once, on whichever comes first: the "load" event, the style
  // reporting itself loaded, or a timer. Seen in the field: a first visit where
  // "load" never fired (no first frame while the tab was not visible), leaving the
  // map without vehicle, route or beam until a reload. Never depend on one event.
  let mapReady = false;
  const setupMap = (): void => {
    if (map === null || mapReady || !map.isStyleLoaded()) return;
    mapReady = true;
    // The route sits under the beam and under every marker.
    map.addSource("route", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
    map.addLayer({
      id: "route-casing", type: "line", source: "route",
      layout: { "line-join": "round", "line-cap": "round" },
      paint: { "line-color": "#0A0A0A", "line-width": 10, "line-opacity": 0.9 },
    });
    map.addLayer({
      id: "route-line", type: "line", source: "route",
      layout: { "line-join": "round", "line-cap": "round" },
      paint: { "line-color": "#FFFFFF", "line-width": 4.5 },
    });
    // The alternative, while the driver is choosing: same line, half present.
    map.addSource("route-alt", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
    map.addLayer({
      id: "route-alt-line", type: "line", source: "route-alt",
      layout: { "line-join": "round", "line-cap": "round" },
      paint: { "line-color": "#FFFFFF", "line-width": 4, "line-opacity": 0.35, "line-dasharray": [1.5, 1.5] },
    });
    installTrafficLayers(map);
    map.addSource("beam", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
    map.addLayer({
      id: "beam-fill", type: "fill", source: "beam",
      paint: { "fill-color": "#FFFFFF", "fill-opacity": 0.055 },
    });
    map.addLayer({
      id: "beam-edge", type: "line", source: "beam",
      paint: { "line-color": "#FFFFFF", "line-opacity": 0.16, "line-width": 1 },
    });
    // A full-screen map must be told its size explicitly: if the container was
    // laid out after construction, MapLibre computes a zero-tile viewport and
    // silently never requests a single tile.
    map.resize();
    if (vehicle !== null) vehicle.addTo(map);
    map.triggerRepaint();
  };
  map.on("load", setupMap);
  map.on("styledata", setupMap);
  window.setTimeout(setupMap, 3000);
  window.setTimeout(setupMap, 8000);
  map.on("error", (e: { error?: { message?: string } }) => {
    // Tile and style failures must be visible, not silent — a blank map at
    // 100 km/h is the worst failure this app has.
    console.error("[fanar] map", e.error?.message ?? e);
  });
  window.addEventListener("resize", () => map?.resize());

}

/* ---------------- position ---------------- */

/**
 * DEV ONLY: `?sim=1` replays a synthetic Tirane -> Durres drive instead of reading
 * the GPS. Desktop geolocation puts you wherever you actually are, which makes the
 * Albanian hazard map impossible to look at; this is how the app gets demonstrated
 * and how the drive-replay harness exercises the same code path.
 */
const SIM = params.get("sim") !== null;
const SIM_START = { lat: 41.3275, lon: 19.8187 };
const SIM_COURSE = 262;
/** `?kmh=` sets the simulated speed, so the slow-speed states can be reviewed. */
const SIM_SPEED = Number(params.get("kmh") ?? 90) / 3.6;
let simTimer: number | null = null;
let simSeconds = 0;

function simDestination(brgDeg: number, distM: number): { lat: number; lon: number } {
  const R = 6371008.8;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const toDeg = (r: number) => (r * 180) / Math.PI;
  const d = distM / R;
  const b = toRad(brgDeg);
  const la1 = toRad(SIM_START.lat);
  const lo1 = toRad(SIM_START.lon);
  const la2 = Math.asin(Math.sin(la1) * Math.cos(d) + Math.cos(la1) * Math.sin(d) * Math.cos(b));
  const lo2 = lo1 + Math.atan2(Math.sin(b) * Math.sin(d) * Math.cos(la1), Math.cos(d) - Math.sin(la1) * Math.sin(la2));
  return { lat: toDeg(la2), lon: ((toDeg(lo2) + 540) % 360) - 180 };
}

function startSim(): void {
  if (simTimer !== null) return;
  // `?gps=0` makes the simulated receiver fail, so the no-fix state can be reviewed.
  if (params.get("gps") === "0") { onFixError(); return; }
  simTimer = window.setInterval(() => {
    simSeconds += 1;
    let p: { lat: number; lon: number };
    let course = SIM_COURSE;
    if (simRoute !== null) {
      const along = simRoute.alongAt + SIM_SPEED * (simSeconds - simRoute.tick);
      const q = pointAlong(simRoute.idx, along);
      p = { lat: q.lat, lon: q.lon };
      course = q.bearing;
    } else {
      p = simDestination(SIM_COURSE, SIM_SPEED * simSeconds);
    }
    onFix({
      coords: {
        latitude: p.lat, longitude: p.lon, accuracy: 6,
        heading: course, speed: SIM_SPEED, altitude: null,
        altitudeAccuracy: null, toJSON: () => ({}),
      },
      timestamp: Date.now(),
      toJSON: () => ({}),
    } as unknown as GeolocationPosition);
  }, 1000);
}

function startWatch(): void {
  if (SIM) { startSim(); return; }
  if (watchId !== null) navigator.geolocation.clearWatch(watchId);
  watchId = navigator.geolocation.watchPosition(onFix, onFixError, {
    enableHighAccuracy: true,
    maximumAge: 2000,
    timeout: 15_000,
  });
}

function onFixError(): void {
  const el = $("fixState");
  el.dataset["fix"] = "0";
  el.textContent = "PA GPS";
  el.hidden = false;
}

let lastPos: GeolocationPosition | null = null;
let lastOverAt = 0;

/** The strip appears after two seconds at rest and vanishes on the first
 *  movement. Called per fix AND from the 1 s heartbeat: a parked car can go
 *  minutes without a new fix, and the menu must not wait for one. */
function updateStationary(f: Fix, nowMs = f.at): void {
  if (f.speedMps < 1.5) {
    stoppedSince ??= f.at;
    screens.setStationary(nowMs - stoppedSince >= 2000);
  } else {
    stoppedSince = null;
    screens.setStationary(false);
  }
}
/** Anonymous drive summary, posted at the end. */
const driveStats = { startedAt: 0, alerts: 0, reports: 0, navigated: false };

function onFix(pos: GeolocationPosition): void {
  lastPos = pos;
  const c = pos.coords;
  const fixEl = $("fixState");
  fixEl.dataset["fix"] = "1";
  fixEl.hidden = true; // a good fix is the normal state and needs no badge

  fix = {
    lat: c.latitude,
    lon: c.longitude,
    // GPS course over ground only. The compass reads the dashboard magnet.
    course: c.heading !== null && Number.isFinite(c.heading) ? c.heading : null,
    speedMps: c.speed !== null && Number.isFinite(c.speed) ? Math.max(0, c.speed) : 0,
    at: Date.now(),
  };

  const kmh = Math.round(fix.speedMps * 3.6);
  $("speedValue").textContent = String(kmh);
  // Speed limit plate: the OSM-tagged limit on the road under the car, and the
  // readout inverts when the car is clearly over it. A sign, not a lecture.
  limits.track(fix);
  const limit = limits.limitAt(fix, fix.course);
  const plate = $("speedLimit");
  plate.hidden = limit === null;
  if (limit !== null) plate.textContent = String(limit);
  const over = limit !== null && kmh > limit * 1.1 + 2;
  $("speed").dataset["over"] = over ? "1" : "0";
  // One quiet word when the car goes over, at most every 45 s. Never a nag.
  if (over && fix.at - lastOverAt > 45_000) {
    lastOverAt = fix.at;
    if (!voice.speakSequence([navClipUrl("kufiri")], 0)) voice.chime("far");
  }
  driveStats.alerts = engine.announcedCount;
  document.documentElement.dataset["glare"] = "off";

  updateStationary(fix);

  // Navigation first: it decides how "ahead" is measured for this fix.
  const aheadOnRoute = nav.tick(fix);
  engine.setAhead(aheadOnRoute === null ? null : (alert) => aheadOnRoute(alert));

  if (map !== null && !browsing && freeLook) vehicle?.setLngLat([fix.lon, fix.lat]);
  if (map !== null && !browsing && !freeLook) {
    // Hold the vehicle low in the frame so the screen is mostly the road AHEAD.
    // Centred, three quarters of the display is road already travelled.
    map.jumpTo({
      center: [fix.lon, fix.lat],
      bearing: fix.course ?? map.getBearing(),
      zoom: fix.speedMps > 19 ? 13.9 : 15.2,
      padding: { top: Math.round(window.innerHeight * 0.26), bottom: 0, left: 0, right: 0 },
    });
    vehicle?.setLngLat([fix.lon, fix.lat]);
    // Above 20 km/h the map stops being something you read.
    setLabels(fix.speedMps <= 5.6);
    drawBeam(fix);
  }

  trackMovement(fix);
  if (!SIM) trafficSampler.sample(fix);   // the simulator must never feed the network
  void maybeSync(fix);
  engine.prune(fix.at);
  render(fix);
  announce(fix);
}

/** Paints the lookahead corridor the alert filter actually uses: a wedge that opens
 *  to the 150 m cross-track limit and then runs parallel to the end of the lookahead.
 *  It grows and shrinks with speed because the filter does. */
function drawBeam(f: Fix): void {
  if (map === null) return;
  const src = map.getSource("beam") as maplibregl.GeoJSONSource | undefined;
  if (src === undefined) return;
  src.setData(beamPolygon(f));
  // The beam is the at-speed corridor. Below ~40 km/h the 800 m floor, drawn at a
  // close zoom, would cover a third of the map, so it fades toward the edge line.
  const t = Math.min(1, Math.max(0, (f.speedMps - 4) / 8)); // 0 at ~15 km/h, 1 at ~43 km/h
  if (map.getLayer("beam-fill") !== undefined) {
    map.setPaintProperty("beam-fill", "fill-opacity", 0.02 + 0.035 * t);
    map.setPaintProperty("beam-edge", "line-opacity", 0.10 + 0.08 * t);
  }
}

let labelsOn: boolean | null = null;

function setLabels(on: boolean): void {
  if (map === null || labelsOn === on || !map.isStyleLoaded()) return;
  labelsOn = on;
  for (const id of LABEL_LAYERS) {
    if (map.getLayer(id) !== undefined) {
      map.setLayoutProperty(id, "visibility", on ? "visible" : "none");
    }
  }
}

/** Remembers where we were ten minutes ago, so the server can tell a moving
 *  confirmation from an armchair one. */
function trackMovement(f: Fix): void {
  const cell = encodeGeohash(f.lat, f.lon, 6);
  if (lastGh6 === null || f.at - lastGh6.at > 600_000) lastGh6 = { cell, at: f.at };
}

async function maybeSync(f: Fix): Promise<void> {
  if (syncing) return;
  if (Date.now() - feed.lastSyncAt < 20_000) return;
  syncing = true;
  try {
    const { alerts, stale } = await feed.sync(f.lat, f.lon);
    for (const a of alerts) engine.upsert(a);
    const tsrc = map?.getSource("traffic") as maplibregl.GeoJSONSource | undefined;
    tsrc?.setData(trafficGeoJson(feed.traffic.values()));
    const staleEl = $("stale");
    if (stale && feed.lastSyncAt > 0) {
      staleEl.hidden = false;
      staleEl.textContent = `Të dhëna nga ${new Date(feed.lastSyncAt).toLocaleTimeString("sq-AL", { hour: "2-digit", minute: "2-digit" })}`;
    } else if (!stale) {
      staleEl.hidden = true;
    }
  } finally {
    syncing = false;
  }
}

/* ---------------- rendering ---------------- */

/** Severity rank for stacking: the plate a driver must see is drawn on top. */
const SEVERITY_RANK = { info: 0, kujdes: 1, rrezik: 2 } as const;

/** Two pins closer than this fraction of their mean diameter share a post. */
const STACK_OVERLAP = 0.8;
/** Plates below the top one peek out by this fraction of the top plate's size.
 *  A triangle's full-width base hides most of what sits behind it, so the step
 *  has to be generous for the lower plate's shape to read at all. */
const STACK_STEP = 0.44;
/** More than this many plates on one post collapses into a count. Two is the
 *  honest ceiling: a third plate hides behind the first, and no driver parses
 *  three stacked signs at 90 km/h anyway. */
const STACK_MAX = 2;

type Plate = {
  v: Visible;
  decay: ReturnType<typeof decayState>;
  style: (typeof SEVERITY_STYLE)[keyof typeof SEVERITY_STYLE];
  size: number;
};

/**
 * Groups pins that would draw on top of each other into one signpost.
 * Greedy over screen distance; `visible` is nearest-first, so a post is anchored
 * at the hazard the driver reaches first, and plates stack by severity.
 */
function stackPlates(plates: Plate[], project: (p: Plate) => { x: number; y: number }): Plate[][] {
  const pts = plates.map(project);
  const taken = new Set<number>();
  const groups: Plate[][] = [];
  for (let i = 0; i < plates.length; i++) {
    if (taken.has(i)) continue;
    taken.add(i);
    const group = [plates[i] as Plate];
    for (let j = i + 1; j < plates.length; j++) {
      if (taken.has(j)) continue;
      const a = pts[i] as { x: number; y: number };
      const b = pts[j] as { x: number; y: number };
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      const limit = (((plates[i] as Plate).size + (plates[j] as Plate).size) / 2) * STACK_OVERLAP;
      if (d < limit) { taken.add(j); group.push(plates[j] as Plate); }
    }
    groups.push(group);
  }
  return groups;
}

function render(f: Fix): void {
  if (map === null || gl === null) return;
  const m = map;
  const visible: Visible[] = browsing
    ? engine.all().map((alert) => ({
        alert, tth: Infinity,
        distance: haversine({ lat: f.lat, lon: f.lon }, { lat: alert.lat, lon: alert.lon }),
      })).sort((a, b) => a.distance - b.distance)
    : engine.visible(f);

  const plates: Plate[] = visible.map((v) => {
    const decay = decayState(v.alert.type, v.alert.createdAt, v.alert.expiresAt, f.at);
    const style = SEVERITY_STYLE[decay.effectiveSeverity];
    return { v, decay, style, size: style.diameterPt * decay.sizeScale };
  });

  const groups = stackPlates(plates, (p) => m.project([p.v.alert.lon, p.v.alert.lat]));
  const keep = new Set<string>();

  for (const group of groups) {
    const anchor = group[0] as Plate;
    // Most severe on top, and among equals the nearer one: ascending sort so the
    // last element paints last. Farther-first inside a band puts the near one on top.
    const ordered = [...group].sort((a, b) =>
      SEVERITY_RANK[a.decay.effectiveSeverity] - SEVERITY_RANK[b.decay.effectiveSeverity] ||
      b.v.distance - a.v.distance,
    );
    const shown = ordered.slice(-STACK_MAX);
    const hidden = ordered.length - shown.length;
    const key = ordered.map((p) => p.v.alert.id).join("|");
    keep.add(key);

    let marker = markers.get(key);
    if (marker === undefined) {
      const el = document.createElement("div");
      el.className = "pin";
      el.addEventListener("click", () => {
        if (!browsing) return;
        const topId = el.dataset["top"];
        const a = engine.all().find((x) => x.id === topId);
        if (a !== undefined) screens.showDetail(a);
      });
      marker = new gl.Marker({ element: el }).setLngLat([anchor.v.alert.lon, anchor.v.alert.lat]).addTo(m);
      markers.set(key, marker);
    }
    const el = marker.getElement();
    const top = shown[shown.length - 1] as Plate;
    el.dataset["top"] = top.v.alert.id;
    const step = top.size * STACK_STEP;
    // The box grows downward to hold the peeking plates.
    el.style.width = `${top.size}px`;
    el.style.height = `${top.size + step * (shown.length - 1)}px`;
    el.classList.toggle("pin--stack", shown.length > 1);

    // Rebuild the plates only when the post's membership or a severity band changes.
    const want = shown.map((p) => `${p.v.alert.type}:${p.style.inverted ? 1 : 0}:${p.v.alert.hold === true ? "h" : ""}`).join(",") + `+${hidden}`;
    if (el.dataset["mark"] !== want) {
      el.dataset["mark"] = want;
      el.innerHTML =
        shown.map((p) => `<div class="plate${p.v.alert.hold === true ? " plate--hold" : ""}">${pinMark(p.v.alert.type, p.v.alert.hold === true ? false : p.style.inverted)}</div>`).join("") +
        (hidden > 0 ? `<span class="pin__count">+${hidden}</span>` : "");
    }

    const plateEls = el.querySelectorAll<HTMLElement>(".plate");
    shown.forEach((p, i) => {
      const pe = plateEls[i];
      if (pe === undefined) return;
      // Plates below the top sit progressively lower and smaller, like a signpost.
      const depth = shown.length - 1 - i;
      pe.style.width = `${p.size}px`;
      pe.style.height = `${p.size}px`;
      pe.style.top = `${depth * step}px`;
      pe.style.left = `${(top.size - p.size) / 2}px`;
      // Hold band: never more than half-present, never inverted, never pulsing —
      // a claim we do not yet stand behind must look like one.
      const hold = p.v.alert.hold === true;
      pe.style.opacity = String(hold ? Math.min(0.45, p.decay.opacity) : p.decay.opacity);
      pe.style.zIndex = String(i);
      setRing(pe, p.decay.ringDegrees, p.decay.t);
      const inner = pe.firstElementChild as HTMLElement | null;
      if (inner !== null) {
        // Only the top plate pulses; a whole post throbbing is noise.
        inner.style.animation =
          depth === 0 && !hold && p.decay.pulses && p.style.pulseHz > 0
            ? `${p.decay.effectiveSeverity === "rrezik" ? "fanar-pulse-strong" : "fanar-pulse"} ${1 / p.style.pulseHz}s ease-in-out infinite`
            : "none";
      }
    });
  }

  for (const [id, marker] of markers) {
    if (!keep.has(id)) { marker.remove(); markers.delete(id); }
  }
}

let bandTimer: number | null = null;

function announce(f: Fix): void {
  for (const ann of engine.due(f)) {
    const url = clipUrl(ann.alert.type, ann.stage);
    // No clip (not recorded yet, or failed to load): an earcon still turns the
    // driver's eyes to the band. Silence is the one thing this must never be.
    if (voice.hasClip(url)) voice.speak(url);
    else voice.chime("hazard");
    showBand(ann.alert, f);
  }
}

function showBand(alert: Alert, f: Fix): void {
  activeAlert = alert;
  const band = $("alertBand");
  // The full sign, black on white — the same object the driver just saw on the map,
  // and a road sign is what a driver reads fastest. The bare glyph read as an
  // abstract shape at this size.
  $("alertGlyph").innerHTML = pinMark(alert.type, false);
  $("alertLabel").textContent = HAZARDS[alert.type].label;
  // A number forces reading. Words only appear when slow enough to read them.
  band.dataset["slow"] = f.speedMps * 3.6 < 30 ? "1" : "0";
  band.hidden = false;
  $("drive").dataset["alert"] = "1";
  requestAnimationFrame(() => { band.dataset["open"] = "1"; });

  const fill = $("alertFill");
  fill.style.transition = "none";
  fill.style.width = "100%";
  requestAnimationFrame(() => {
    fill.style.transition = "width 9s linear";
    fill.style.width = "0%";
  });

  if (bandTimer !== null) window.clearTimeout(bandTimer);
  bandTimer = window.setTimeout(() => {
    band.dataset["open"] = "0";
    delete $("drive").dataset["alert"];
    activeAlert = null;
    window.setTimeout(() => { band.hidden = true; }, 220);
  }, 9000);
}

/* ---------------- reporting ---------------- */

const zone = $("reportZone");

zone.addEventListener("pointerdown", (ev: PointerEvent) => {
  ev.preventDefault();
  zone.setPointerCapture(ev.pointerId);
  const profile = pickProfile({
    speedMps: fix?.speedMps ?? 0,
    hourLocal: new Date().getHours(),
  });
  void radial.open(profile).then((choice) => {
    if (choice === "more") openGrid(profile);
    else if (choice !== null) commitReport(choice);
  });
});

zone.addEventListener("pointermove", (ev: PointerEvent) => {
  if (radial.isOpen) radial.move(ev.clientX, ev.clientY);
});

const release = (): void => { if (radial.isOpen) radial.release(); };
zone.addEventListener("pointerup", release);
zone.addEventListener("pointercancel", release);

function openGrid(profile: ReturnType<typeof pickProfile>): void {
  const grid = document.createElement("div");
  grid.className = "grid";

  const title = document.createElement("h2");
  title.className = "grid__title";
  title.textContent = "Çfarë ka në rrugë?";
  grid.appendChild(title);

  const cells = document.createElement("div");
  cells.className = "grid__cells";
  for (const type of overflowTypes(profile)) {
    const cell = document.createElement("button");
    cell.className = "grid__cell";
    cell.type = "button";
    cell.innerHTML = `<span class="grid__glyph">${glyphMark(type)}</span><span>${HAZARDS[type].label}</span>`;
    cell.addEventListener("click", () => { grid.remove(); commitReport(type); });
    cells.appendChild(cell);
  }
  grid.appendChild(cells);

  const cancel = document.createElement("button");
  cancel.className = "grid__cancel";
  cancel.type = "button";
  cancel.textContent = "ANULO";
  cancel.addEventListener("click", () => grid.remove());
  grid.appendChild(cancel);
  $("drive").appendChild(grid);
}

/** Queue locally, show a 6 s undo, and only then send. The common mis-tap never
 *  reaches the network. */
function commitReport(type: HazardType): void {
  if (fix === null) return;
  const clientKey = crypto.randomUUID();
  driveStats.reports++;
  session.enqueue("/v1/reports", {
    clientKey,
    type,
    lat: fix.lat,
    lon: fix.lon,
    accuracyM: 10,
    reporterCourse: fix.course,
    speedMps: fix.speedMps,
  });

  const undo = $("undo");
  $("undoGlyph").innerHTML = glyphMark(type);
  $("undoLabel").textContent = HAZARDS[type].label;
  undo.hidden = false;
  const fill = $("undoFill");
  fill.style.transition = "none";
  fill.style.width = "100%";
  requestAnimationFrame(() => {
    fill.style.transition = "width 6s linear";
    fill.style.width = "0%";
  });

  let cancelled = false;
  const cancel = (): void => {
    cancelled = true;
    session.cancelQueued((i) => i.body.includes(clientKey));
    undo.hidden = true;
    undo.removeEventListener("pointerdown", cancel);
  };
  undo.addEventListener("pointerdown", cancel);

  window.setTimeout(() => {
    undo.removeEventListener("pointerdown", cancel);
    undo.hidden = true;
    if (!cancelled) void session.flush();
  }, 6000);
}

/* ---------------- lifecycle ---------------- */

/** Assume every app switch cold-restarted audio, the wake lock and the GPS watch. */
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState !== "visible") return;
  if ($("drive").hidden) return;

  void wakeLock.acquire();
  startWatch();
  void voice.resume().then((status) => {
    // If the context will not leave "interrupted", ask for one tap rather than
    // failing silently — silent failure in a driving app is the worst outcome.
    $("resumeTap").hidden = status !== "interrupted";
  });
  void session.flush();
});

$("resumeTap").addEventListener("click", () => {
  voice.unlockFromGesture();
  void voice.resume();
  $("resumeTap").hidden = true;
});

window.addEventListener("online", () => { void session.flush(); $("offline").hidden = true; });
window.addEventListener("offline", () => { $("offline").hidden = false; });
$("offline").hidden = navigator.onLine;

/**
 * Swipe votes while the band is up: the whole screen is the target, no aim.
 * Down = "është aty" (pull it toward you), up = "nuk është" (push it away).
 * Only the map area counts, so the report zone and the strip keep their own taps.
 */
let swipeStart: { x: number; y: number } | null = null;
$("map").addEventListener("pointerdown", (ev: PointerEvent) => {
  if (activeAlert === null || browsing || screens.isOpen) return;
  swipeStart = { x: ev.clientX, y: ev.clientY };
});
$("map").addEventListener("pointerup", (ev: PointerEvent) => {
  if (swipeStart === null || activeAlert === null) { swipeStart = null; return; }
  const dy = ev.clientY - swipeStart.y;
  swipeStart = null;
  if (Math.abs(dy) < 40) return;
  const target = activeAlert;
  void castVote(target.id, dy > 0 ? "confirm" : "reject");
  // Acknowledge on the band itself: a voted band retracts early.
  if (bandTimer !== null) window.clearTimeout(bandTimer);
  bandTimer = window.setTimeout(() => {
    $("alertBand").dataset["open"] = "0";
    delete $("drive").dataset["alert"];
    activeAlert = null;
    window.setTimeout(() => { $("alertBand").hidden = true; }, 220);
  }, 350);
});

/** Long-press on the map while browsing: "Shko këtu". */
let pressTimer: number | null = null;
let pressAt: { x: number; y: number } | null = null;
$("map").addEventListener("pointerdown", (ev: PointerEvent) => {
  if (!browsing || map === null) return;
  pressAt = { x: ev.clientX, y: ev.clientY };
  if (pressTimer !== null) window.clearTimeout(pressTimer);
  pressTimer = window.setTimeout(() => {
    if (map === null || pressAt === null) return;
    const rect = $("map").getBoundingClientRect();
    const ll = map.unproject([pressAt.x - rect.left, pressAt.y - rect.top]);
    screens.close();
    void startNavigation({ name: "Pika në hartë", lat: ll.lat, lon: ll.lng });
  }, 650);
});
$("map").addEventListener("pointermove", (ev: PointerEvent) => {
  if (pressAt !== null && Math.hypot(ev.clientX - pressAt.x, ev.clientY - pressAt.y) > 12 && pressTimer !== null) { window.clearTimeout(pressTimer); pressTimer = null; }
});
for (const evName of ["pointerup", "pointercancel"] as const) {
  $("map").addEventListener(evName, () => { if (pressTimer !== null) { window.clearTimeout(pressTimer); pressTimer = null; } pressAt = null; });
}

/** Errors on phones nobody here holds: one anonymous line per error, capped. */
let errorsSent = 0;
window.addEventListener("error", (ev) => {
  if (errorsSent++ >= 5 || SIM) return;
  const body = JSON.stringify({ kind: "error", message: String(ev.message).slice(0, 300), stack: String(ev.error?.stack ?? "").slice(0, 1500), version: APP_VERSION });
  navigator.sendBeacon?.("/v1/telemetry", new Blob([body], { type: "application/json" }));
});
window.addEventListener("unhandledrejection", (ev) => {
  if (errorsSent++ >= 5 || SIM) return;
  const reason = ev.reason as { message?: string; stack?: string } | undefined;
  const body = JSON.stringify({ kind: "error", message: `unhandled: ${String(reason?.message ?? ev.reason).slice(0, 280)}`, stack: String(reason?.stack ?? "").slice(0, 1500), version: APP_VERSION });
  navigator.sendBeacon?.("/v1/telemetry", new Blob([body], { type: "application/json" }));
});

/** Back to the welcome screen. The next "Nis udhëtimin" re-runs the gesture. */
function endDrive(): void {
  if (driveStats.startedAt > 0) {
    const body = JSON.stringify({
      kind: "session", minutes: Math.round((Date.now() - driveStats.startedAt) / 60_000), alerts: driveStats.alerts,
      reports: driveStats.reports, navigated: driveStats.navigated || nav.active, platform: isIOS ? "ios" : "other", installed: isStandalone,
    });
    if (!SIM) navigator.sendBeacon?.("/v1/telemetry", new Blob([body], { type: "application/json" }));
    driveStats.startedAt = 0;
  }
  nav.stop();
  trafficSampler.end();
  if (watchId !== null) { navigator.geolocation.clearWatch(watchId); watchId = null; }
  if (simTimer !== null) { window.clearInterval(simTimer); simTimer = null; simSeconds = 0; }
  void wakeLock.release();
  screens.setStationary(false);
  $("drive").hidden = true;
  $("welcome").hidden = false;
  engine.resetAnnouncements();
}

// The chrome carries drawn marks, not typed characters.
document.querySelectorAll<HTMLElement>("[data-mark-f]").forEach((el) => { el.innerHTML = MARK_F; });
$("reportIcon").innerHTML = MARK_REPORT;
$("topMark").innerHTML = MARK_F;

if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js").catch(() => undefined);
  });
}
void navigator.storage?.persist?.().catch(() => undefined);
